/*
  "Make it a kit" upsell list on the product page.

  Every ticked card is folded into the main add-to-cart request, so the product
  and its add-ons land in the cart as one atomic call. A card is locked to one
  variant, and Liquid renders its price (discount metafields included) - the
  totals here only read those numbers back.

  Which add-on variant goes with which colour of the main product is a merchant
  decision, not a rule, so Liquid hands over the whole table as data-kit-map and
  this only looks the answer up. Every add-on used by any colour already has a
  card in the DOM; changing colour shows, hides and re-points them.
*/

/* Two sections can pull this file in on one page - the buy box and the
   standalone add-to-cart. A bare top-level class would throw
   "Identifier 'ProductKit' has already been declared" on the second load,
   so the whole thing lives in a scope that simply bows out. */
(() => {
  if (customElements.get('product-kit')) return;

  let mirroring = false;

  class ProductKit extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.form = document.getElementById(this.dataset.formId);
      this.cards = Array.from(this.querySelectorAll('[data-kit-card]'));
      this.onFormData = this.onFormData.bind(this);
      this.onColorChange = this.onColorChange.bind(this);

      this.cards.forEach((card) => {
        card.querySelector('[data-kit-checkbox]')?.addEventListener('change', () => this.onCardChange(card));
        this.initVariantSelector(card);
      });

      this.initImpactInfo();

      this.form?.addEventListener('formdata', this.onFormData);

      /*
        The buy box owns the colour, and both kits on the page follow that one
        picker - the standalone section sells the same product further down.
      */
      if (this.dataset.kitMap || this.dataset.matchIndex) {
        try {
          this.map = this.dataset.kitMap ? JSON.parse(this.dataset.kitMap) : null;
          this.mainColors = this.dataset.mainColors ? JSON.parse(this.dataset.mainColors) : null;
        } catch {
          this.map = null;
          this.mainColors = null;
        }
        this.picker = document.querySelector('variant-radios');
        this.picker?.addEventListener('change', this.onColorChange);
      }

      this.syncHeading();
      this.updateTotals();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('formdata', this.onFormData);
      this.picker?.removeEventListener('change', this.onColorChange);
    }

    /*
      Read the option values off the checked radios rather than the picker's
      currentVariant: both listen to the same change event and the order between
      them is not ours to rely on. This mirrors VariantSelects.updateOptions, so
      the joined value matches the key Liquid wrote from variant.options.
    */
    onColorChange() {
      const values = Array.from(this.picker.querySelectorAll('fieldset')).map(
        (fieldset) => fieldset.querySelector('input:checked')?.value
      );

      this.map ? this.applyPins(values.join(' / ')) : this.applyColor(values[Number(this.dataset.matchIndex)]);
      this.updateTotals();
    }

    /* Merchant-pinned add-on variants: one list per colour of the main product. */
    applyPins(key) {
      const pins = this.map[key] || {};
      let shown = 0;
      const limit = Number(this.dataset.limit) || this.cards.length;

      this.cards.forEach((card) => {
        const pinned = pins[card.dataset.productId];
        const variants = this.variantsOf(card);
        let variant = pinned && variants.find((entry) => entry.id === pinned);

        if (variant && !variant.available) {
          const substitute = this.mainColors
            ? variants.find((entry) => entry.available && this.mainColors.includes(entry.color))
            : variants.find((entry) => entry.available);
          variant = substitute || variant;
        }

        /* No pin for this colour, or nothing in stock to show instead: park the card. */
        if (!variant || !variant.available || shown >= limit) {
          this.hideCard(card);
          return;
        }

        card.hidden = false;
        if (!card.dataset.manualVariant) this.renderCard(card, variant);
        shown += 1;
      });

      this.syncHeading();
    }

    /* The heading sits outside the element: with every card parked it would title an empty list. */
    syncHeading() {
      const heading = this.parentElement?.querySelector(':scope > [data-kit-heading]');
      if (heading) heading.hidden = this.cards.every((card) => card.hidden);
    }

    /*
      Nothing pinned, so every add-on follows the colour on screen. One that is
      not made in that colour keeps the variant it was rendered with.
    */
    applyColor(color) {
      if (!color) return;

      this.cards.forEach((card) => {
        if (card.dataset.manualVariant) return;
        const variant = this.variantsOf(card).find((entry) => entry.color === color);
        if (variant) this.renderCard(card, variant);
      });
    }

    /*
      Merchant setting only: a card gets a "change variant" trigger + modal when
      the block enables it. Picking one there pins the card the same way a merchant
      pin does - onColorChange stops touching it until the page reloads.
    */
    initVariantSelector(card) {
      const trigger = card.querySelector('[data-kit-variant-trigger]');
      const modal = card.querySelector('[data-kit-variant-modal]');
      if (!trigger || !modal) return;

      trigger.addEventListener('click', () => modal.classList.add('show'));
      modal.querySelector('[data-kit-variant-close]')?.addEventListener('click', () => modal.classList.remove('show'));
      document.addEventListener('click', (event) => {
        if (!event.target.closest('.kit-card__variant-modal, .kit-card__variant-trigger')) {
          modal.classList.remove('show');
        }
      });

      modal.querySelectorAll('.variant-option').forEach((opt) => {
        opt.addEventListener('click', () => this.selectManualVariant(card, opt));
      });
    }

    selectManualVariant(card, opt) {
      if (opt.dataset.available !== 'true') return;

      this.renderCard(card, {
        id: opt.dataset.id,
        price: opt.dataset.price,
        comparePrice: opt.dataset.comparePrice,
        priceFormatted: opt.dataset.priceFormatted,
        comparePriceFormatted: opt.dataset.comparePriceFormatted,
        discountPercent: opt.dataset.discountPercent,
        image: opt.dataset.image,
        title: opt.dataset.title,
      });
      card.dataset.manualVariant = 'true';

      card.querySelectorAll('.variant-option').forEach((entry) => entry.classList.toggle('current', entry === opt));
      card.querySelector('[data-kit-variant-modal]')?.classList.remove('show');

      this.updateTotals();
    }

    /* Hidden cards must not stay ticked, or they would be added to the cart unseen. */
    hideCard(card) {
      card.hidden = true;
      const checkbox = card.querySelector('[data-kit-checkbox]');
      if (checkbox) checkbox.checked = false;
      card.classList.remove('kit-card--selected');
      if (card === this.impactCard) this.syncImpactInfo();
    }

    variantsOf(card) {
      if (!card.kitVariants) {
        try {
          card.kitVariants = JSON.parse(card.querySelector('[data-kit-variants]')?.textContent || '[]');
        } catch {
          card.kitVariants = [];
        }
      }
      return card.kitVariants;
    }

    renderCard(card, variant) {
      card.dataset.variantId = variant.id;
      card.dataset.price = variant.price;
      card.dataset.comparePrice = variant.comparePrice;

      const price = card.querySelector('[data-kit-price-current]');
      if (price) price.innerHTML = variant.priceFormatted || '';

      const compare = card.querySelector('[data-kit-price-compare]');
      const showCompare = variant.comparePrice > variant.price;
      if (compare) {
        compare.innerHTML = showCompare ? variant.comparePriceFormatted : '';
        compare.hidden = !showCompare;
      }

      /* Tiles and rows spell it "10%off", as their mockups do - the badge carries
         its own wording so a redraw doesn't swap it back to the list's "10% off". */
      const badge = card.querySelector('[data-kit-badge]');
      if (badge) {
        badge.textContent = `${variant.discountPercent}${badge.dataset.kitBadgeSuffix || '% off'}`;
        badge.hidden = variant.discountPercent <= 0;
      }

      const title = card.querySelector('[data-kit-variant-title]');
      if (title) {
        /* The selectable trigger wraps its text in a span (for the ↓ marker); the
           plain label doesn't, so fall back to the element itself. */
        const titleText = title.querySelector('span') || title;
        titleText.textContent = variant.title || '';
        title.hidden = !variant.title;
      }

      /* srcset wins over src, so it has to go before the swap. */
      const image = card.querySelector('.kit-card__image');
      if (image && variant.image) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = variant.image;
      }

      if (card === this.impactCard) this.filterImpactMedia(variant.title);
    }

    isSelected(card) {
      return Boolean(card.querySelector('[data-kit-checkbox]')?.checked);
    }

    get selectedCards() {
      return this.cards.filter((card) => !card.hidden && this.isSelected(card));
    }

    get hasSelection() {
      return this.selectedCards.length > 0;
    }

    /* Both totals are in cents, matching the variant JSON the buy box works with. */
    get total() {
      return this.selectedCards.reduce((sum, card) => sum + (Number(card.dataset.price) || 0), 0);
    }

    get compareTotal() {
      return this.selectedCards.reduce(
        (sum, card) => sum + (Number(card.dataset.comparePrice) || Number(card.dataset.price) || 0),
        0
      );
    }

    onCardChange(card) {
      card.classList.toggle('kit-card--selected', this.isSelected(card));
      if (card === this.impactCard) this.syncImpactInfo();
      this.mirrorToOtherKits(card);
      this.updateTotals();
    }

    /*
      The Arc Impact text + "read more" popup sit under the "Make it a kit"
      heading rather than on the card itself, so they only make sense once the
      customer has actually opted into that one add-on - otherwise they'd be
      explaining a product that isn't going into the cart.
    */
    initImpactInfo() {
      this.impactInfo = this.querySelector('[data-kit-impact-info]');
      if (!this.impactInfo) return;

      this.impactCard = this.cards.find(
        (card) => card.dataset.productId === this.impactInfo.dataset.kitImpactProductId
      );
      this.syncImpactInfo();

      const sliderId = this.impactInfo.dataset.kitImpactSliderId;
      const slider = sliderId && document.getElementById(sliderId);
      if (slider && !slider.swiper && typeof Swiper !== 'undefined') {
        new Swiper(slider, {
          slidesPerView: 1.2,
          mousewheel: { forceToAxis: true },
          autoHeight: false,
          spaceBetween: 7,
          breakpoints: {
            750: { slidesPerView: 3, spaceBetween: 10 },
          },
        });
      }

      /* The gallery ships server-rendered for the product's first variant only -
         catch it up to whichever variant the card actually starts on. */
      if (this.impactCard) {
        const initialVariant = this.variantsOf(this.impactCard).find(
          (entry) => String(entry.id) === this.impactCard.dataset.variantId
        );
        if (initialVariant) this.filterImpactMedia(initialVariant.title);
      }
    }

    syncImpactInfo() {
      if (!this.impactInfo || !this.impactCard) return;
      this.impactInfo.hidden = !this.isSelected(this.impactCard);
    }

    /* Mirrors ImpactVariantRadios.filterMedia in impact.js: the modal gallery
       only shows the slides tagged for the variant currently on the card. */
    filterImpactMedia(variantTitle) {
      if (!variantTitle) return;
      const sliderId = this.impactInfo?.dataset.kitImpactSliderId;
      const swiper = sliderId && document.getElementById(sliderId)?.swiper;
      if (swiper) filterSlides(swiper, null, pascalize(variantTitle));
    }

    mirrorToOtherKits(card) {
      if (mirroring) return;
      mirroring = true;

      try {
        const checked = this.isSelected(card);
        document.querySelectorAll('product-kit').forEach((kit) => {
          if (kit !== this) kit.applySelection?.(card.dataset.productId, checked);
        });
      } finally {
        mirroring = false;
      }
    }

    applySelection(productId, checked) {
      const card = this.cards?.find((entry) => entry.dataset.productId === productId && !entry.hidden);
      const checkbox = card?.querySelector('[data-kit-checkbox]');
      if (!checkbox || checkbox.checked === checked) return;

      checkbox.checked = checked;
      card.classList.toggle('kit-card--selected', checked);
      if (card === this.impactCard) this.syncImpactInfo();
      this.updateTotals();
    }

    /*
      A page can carry more than one kit (the buy box and the standalone
      add-to-cart section), so the event bubbles from this element instead of
      document, and only the buy box asks the variant picker to redraw.
    */
    updateTotals() {
      if (this.hasAttribute('data-main-product')) document.querySelector('variant-radios')?.updatePrice();

      this.dispatchEvent(
        new CustomEvent('product-kit:change', {
          bubbles: true,
          detail: { total: this.total, compareTotal: this.compareTotal, hasSelection: this.hasSelection },
        })
      );
    }

    /*
      Rewrites the add-to-cart payload into the items[] form Shopify uses for a
      multi-item add: the product itself becomes items[0], anything already queued
      by another block (the Arc Impact inlay) keeps its place, kit cards follow.
    */
    onFormData(event) {
      if (!this.hasSelection) return;

      const data = event.formData;
      const mainId = data.get('id');

      const queued = Array.from(data.keys())
        .filter((key) => /^items\[\d+\]id$/.test(key))
        .map((key) => data.get(key))
        .filter(Boolean);

      Array.from(data.keys())
        .filter((key) => /^items\[\d+\](?!\[)/.test(key))
        .forEach((key) => data.delete(key));

      const quantity = data.get('quantity') || 1;
      const properties = Array.from(data.keys())
        .filter((key) => key.startsWith('properties['))
        .map((key) => [key.slice('properties['.length, -1), data.get(key)]);

      data.delete('id');
      data.delete('quantity');
      properties.forEach(([name]) => data.delete(`properties[${name}]`));

      let index = 0;

      /* A kit-only section has no product of its own, so items[] is the kit alone. */
      if (mainId) {
        data.append('items[0][id]', mainId);
        data.append('items[0][quantity]', quantity);
        properties.forEach(([name, value]) => data.append(`items[0][properties][${name}]`, value));
        index = 1;
      }

      queued.forEach((id) => {
        data.append(`items[${index}][id]`, id);
        data.append(`items[${index}][quantity]`, 1);
        data.append(`items[${index}][properties][_upsell]`, 'Product page');
        index += 1;
      });

      /* The upsell test's variants tag their add-ons as well, so checkout can tell
         which variant sold them - _upsell itself stays as it is for everything
         already reading it. */
      const upsellVariant = this.dataset.upsellVariant;

      this.selectedCards.forEach((card) => {
        const variantId = card.dataset.variantId;
        if (!variantId) return;
        data.append(`items[${index}][id]`, variantId);
        data.append(`items[${index}][quantity]`, 1);
        data.append(`items[${index}][properties][_upsell]`, 'Product page kit');
        if (upsellVariant) data.append(`items[${index}][properties][_upsell_variant]`, upsellVariant);
        index += 1;
      });
    }
  }

  customElements.define('product-kit', ProductKit);
})();
